"use client";

import { motion } from "framer-motion";
import { Email, Visibility, Settings } from "@mui/icons-material";

interface MissionVisionSectionProps {
  mission: {
    title: string;
    icon: string;
    desc: string;
  };
  vision: {
    title: string;
    icon: string;
    desc: string;
  };
  methodology: {
    title: string;
    icon: string;
    desc: string;
  };
}

const iconMap: Record<string, any> = {
  Email,
  Visibility,
  Settings,
};

const MissionVisionSection = ({
  mission,
  vision,
  methodology,
}: MissionVisionSectionProps) => {
  const MissionIcon = iconMap[mission?.icon] || Email;
  const VisionIcon = iconMap[vision?.icon] || Visibility;
  const MethodologyIcon = iconMap[methodology?.icon] || Settings;

  return (
    <section className="bg-tertiary py-[3rem] md:py-[5rem] px-[1rem] md:px-[2rem] w-full overflow-hidden">
      <div className="mx-auto max-w-[120rem] w-full">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-[2rem] md:gap-[3rem]">
          {/* Mission Card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
            className="bg-white/10 backdrop-blur-sm rounded-[1rem] p-[2rem] md:p-[3rem] text-center w-full"
          >
            <div className="flex justify-center mb-[1.5rem] md:mb-[2rem]">
              <div className="w-[4rem] h-[4rem] md:w-[5rem] md:h-[5rem] bg-white/20 rounded-full flex items-center justify-center">
                <MissionIcon className="text-white text-[2.5rem] md:text-[3rem]" />
              </div>
            </div>
            <h3 className="text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] font-bold text-white mb-[1rem] md:mb-[1.5rem]">
              {mission?.title}
            </h3>
            <p className="text-[1rem] sm:text-[1.2rem] md:text-[1.4rem] text-white/90 leading-relaxed">
              {mission?.desc}
            </p>
          </motion.div>

          {/* Vision Card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.04 }}
            className="bg-white/10 backdrop-blur-sm rounded-[1rem] p-[2rem] md:p-[3rem] text-center w-full"
          >
            <div className="flex justify-center mb-[1.5rem] md:mb-[2rem]">
              <div className="w-[4rem] h-[4rem] md:w-[5rem] md:h-[5rem] bg-white/20 rounded-full flex items-center justify-center">
                <VisionIcon className="text-white text-[2.5rem] md:text-[3rem]" />
              </div>
            </div>
            <h3 className="text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] font-bold text-white mb-[1rem] md:mb-[1.5rem]">
              {vision?.title}
            </h3>
            <p className="text-[1rem] sm:text-[1.2rem] md:text-[1.4rem] text-white/90 leading-relaxed">
              {vision?.desc}
            </p>
          </motion.div>

          {/* Methodology Card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="bg-white/10 backdrop-blur-sm rounded-[1rem] p-[2rem] md:p-[3rem] text-center w-full"
          >
            <div className="flex justify-center mb-[1.5rem] md:mb-[2rem]">
              <div className="w-[4rem] h-[4rem] md:w-[5rem] md:h-[5rem] bg-white/20 rounded-full flex items-center justify-center">
                <MethodologyIcon className="text-white text-[2.5rem] md:text-[3rem]" />
              </div>
            </div>
            <h3 className="text-[1.5rem] sm:text-[2rem] md:text-[2.5rem] font-bold text-white mb-[1rem] md:mb-[1.5rem]">
              {methodology?.title}
            </h3>
            <p className="text-[1rem] sm:text-[1.2rem] md:text-[1.4rem] text-white/90 leading-relaxed">
              {methodology?.desc}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MissionVisionSection;
